import React from 'react';

/**
 * Universal CountBadge component for numeric indicators.
 *
 * This is the ONLY way to render a count/number badge in the HR Portal.
 * Do NOT create local count badge components or use inline Tailwind for count pills.
 *
 * @param {number}  count              - The number to display
 * @param {'admin'|'employee'|'neutral'|'orange'|'success'|'warning'} [theme='neutral']
 * @param {boolean} [active=false]     - Inverted styling for active tab/nav context
 * @param {'sm'|'md'} [size='sm']      - sm: tabs/filters, md: section headers
 * @param {'soft'|'solid'} [variant='soft'] - soft = tinted (tabs/dense chrome); solid = filled (panel headers needing contrast)
 * @param {boolean} [overlay=false]    - Absolutely positioned in a corner
 * @param {'outside'|'inside'} [overlayPosition='outside'] - Overlay offset style
 * @param {number}  [max=99]           - Display cap; shows "{max}+" when exceeded
 * @param {string}  [className]        - Extra classes (margins, etc.)
 */

const THEME_COLORS = {
  admin: {
    soft: 'bg-red-100 text-red-600',
    solid: 'bg-red-600 text-white',
    active: 'bg-white text-red-600',
  },
  employee: {
    soft: 'bg-blue-100 text-blue-800',
    solid: 'bg-blue-800 text-white',
    active: 'bg-white text-blue-800',
  },
  neutral: {
    soft: 'bg-gray-100 text-gray-700',
    solid: 'bg-gray-700 text-white',
    active: 'bg-white text-gray-700',
  },
  orange: {
    soft: 'bg-orange-500 text-white',
    solid: 'bg-orange-500 text-white',
    active: 'bg-white text-orange-600',
  },
  success: {
    soft: 'bg-green-100 text-green-700',
    solid: 'bg-green-600 text-white',
    active: 'bg-white text-green-700',
  },
  warning: {
    soft: 'bg-orange-100 text-orange-700',
    solid: 'bg-orange-500 text-white',
    active: 'bg-white text-orange-700',
  },
};

const SIZE_CLASSES = {
  sm: 'min-w-[18px] h-[18px] px-1 text-[11px] font-semibold',
  md: 'min-w-[20px] h-5 px-1.5 text-xs font-medium',
};

export const CountBadge = ({
  count,
  theme = 'neutral',
  active = false,
  size = 'sm',
  variant = 'soft',
  overlay = false,
  overlayPosition = 'outside',
  max = 99,
  className = '',
}) => {
  const colors = THEME_COLORS[theme] || THEME_COLORS.neutral;
  const fillKey = variant === 'solid' ? 'solid' : 'soft';
  const colorClasses = active ? colors.active : colors[fillKey];
  const sizeClasses = SIZE_CLASSES[size] || SIZE_CLASSES.sm;
  const overlayPositionClasses = overlayPosition === 'inside'
    ? 'top-0.5 right-0.5'
    : '-top-1 -right-1';
  const positionClasses = overlay ? `absolute z-10 ${overlayPositionClasses}` : '';
  const display = count > max ? `${max}+` : count;

  return (
    <span
      className={`inline-flex items-center justify-center rounded-full ${sizeClasses} ${colorClasses} ${positionClasses} ${className}`.trim()}
    >
      {display}
    </span>
  );
};

export default CountBadge;
